import { Field, Form, Formik } from 'formik'
import isNil from 'lodash-es/isNil'
import React from 'react'

import { StockTooltip } from '@components/common/StockTooltip'
import { WeightInput } from '@components/common/WeightInput'
import { DashboardPermissionGuard } from '@components/utils/DashboardPermissions'
import {
  Button,
  ButtonGroup,
  CurrencyInput,
  Input,
  ModalBox,
  PriceString,
  ProfitString,
  useModalContext,
} from '@fourthwall/components'

import * as S from './styled'
import type { ProductVariantModalFormValues, ProductVariantModalProps } from './types'
import { productVariantValidationSchema } from './validationSchema'

export const ProductVariantModal = ({
  productVariant,
  isPriceEditable,
  isCompareAtPriceEditable,
  isCreatorDeclaredCostEditable,
  isWeightEditable,
  isSKUEditable,
  onSubmit,
  onStockTooltipChange,
}: ProductVariantModalProps) => {
  const { close } = useModalContext()
  const compareAtPrice = productVariant.compareAtPrice?.value
  const cost = productVariant.cost?.value
  const showButtons =
    isPriceEditable ||
    isCompareAtPriceEditable ||
    isCreatorDeclaredCostEditable ||
    isWeightEditable ||
    isSKUEditable

  const handleOnSubmit = (values: ProductVariantModalFormValues) => {
    onSubmit(values)
    close()
  }

  const renderCompareAtPrice = () => {
    if (!(compareAtPrice || isCompareAtPriceEditable)) return null

    return (
      <S.Column>
        <S.Label>Compare-at price</S.Label>
        {isCompareAtPriceEditable ? (
          <Field
            name="compareAtPrice.value"
            component={CurrencyInput}
            type="number"
            size="medium"
            strikethrough
          />
        ) : (
          <S.CompareAtPrice>
            <PriceString value={compareAtPrice} />
          </S.CompareAtPrice>
        )}
      </S.Column>
    )
  }

  return (
    <ModalBox title={productVariant.attributesDescription} onClose={close}>
      <Formik<ProductVariantModalFormValues>
        initialValues={{
          quantity: productVariant.quantity,
          price: productVariant.price,
          compareAtPrice: productVariant.compareAtPrice,
          creatorDeclaredCost: productVariant.creatorDeclaredCost,
          weight: productVariant.weight,
          sku: productVariant.sku,
        }}
        validationSchema={productVariantValidationSchema}
        onSubmit={handleOnSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <S.Container>
              <S.Content>
                <S.Row>
                  <S.Column>
                    <S.Label>In stock</S.Label>
                    <S.StockTooltipWrapper>
                      <StockTooltip
                        quantity={values.quantity}
                        onChange={(oldQuantity, newQuantity) => {
                          setFieldValue('quantity', newQuantity)
                          onStockTooltipChange(oldQuantity, newQuantity)
                        }}
                      />
                    </S.StockTooltipWrapper>
                  </S.Column>
                  <DashboardPermissionGuard permission="contributions.profit">
                    <S.Column>
                      <S.Label>Sold</S.Label>
                      <div>{productVariant.sold}</div>
                    </S.Column>
                  </DashboardPermissionGuard>
                </S.Row>

                {!isNil(productVariant.externalStock) && (
                  <S.Row>
                    <S.Column>
                      <S.Label>In fulfillment center</S.Label>
                      <div>{productVariant.externalStock}</div>
                    </S.Column>
                  </S.Row>
                )}

                <S.Row>
                  <S.Column>
                    <S.Label>Selling price</S.Label>
                    {isPriceEditable ? (
                      <Field
                        component={CurrencyInput}
                        name="price.value"
                        type="number"
                        size="medium"
                        hideSpinner
                      />
                    ) : (
                      <PriceString value={productVariant.price.value} />
                    )}
                  </S.Column>
                  {!isCreatorDeclaredCostEditable && renderCompareAtPrice()}
                </S.Row>

                {isCreatorDeclaredCostEditable && (
                  <S.Row>
                    <S.Column>
                      <S.Label>Product cost</S.Label>
                      <Field
                        name="creatorDeclaredCost.value"
                        component={CurrencyInput}
                        type="number"
                        size="medium"
                      />
                    </S.Column>
                    {renderCompareAtPrice()}
                  </S.Row>
                )}

                {cost && (
                  <DashboardPermissionGuard permission="contributions.profit">
                    <S.Row>
                      <S.Column>
                        <S.Label>Profit per sale</S.Label>
                        <ProfitString value={values.price.value - cost} />
                      </S.Column>
                    </S.Row>
                  </DashboardPermissionGuard>
                )}

                <S.Row>
                  <S.Column>
                    <S.Label>Weight</S.Label>
                    {isWeightEditable ? (
                      <S.WeightInputContainer>
                        <WeightInput
                          weightName="weight.value"
                          unitName="weight.unit"
                          size="medium"
                          weightWidth="100%"
                          unitWidth="100%"
                          displayError
                        />
                      </S.WeightInputContainer>
                    ) : (
                      <div>
                        {productVariant.weight.value}
                        {productVariant.weight.unit}
                      </div>
                    )}
                  </S.Column>
                </S.Row>

                {isSKUEditable && (
                  <S.Row>
                    <S.Column>
                      <S.Label>SKU or UPC code</S.Label>
                      <Field name="sku" component={Input} size="medium" />
                    </S.Column>
                  </S.Row>
                )}
              </S.Content>

              {showButtons && (
                <ButtonGroup direction="column" fullWidth space="medium">
                  <Button type="submit" appearance="primary" label="Save" fullWidth />
                  <Button appearance="secondary" label="Cancel" fullWidth onClick={close} />
                </ButtonGroup>
              )}
            </S.Container>
          </Form>
        )}
      </Formik>
    </ModalBox>
  )
}
